/* Produktseite 2026 (Vorschau) — hologram card of the band „Echtheit“
   (sections/mbb-pd-echtheit.liquid).

   The behaviour of the home page's hologram card (module.hologramm in mbb-start.js,
   which is not loaded on the product page), copied:
   - turn the card over (button or tap on the card) — only where the card has a back;
     the product page has none (operator, 03.11.2026: the features are explained
     next to it),
   - a click on an ID in the list next to the card ([data-holo-wahl]) — or, on the
     home page's layout, „Nächstes Stück“ — rolls the digits to that ID in the same
     ticks as on the home page (TAKT) and marks it in the list — with the counter's
     clicks of the home page (operator, 03.11.2026; switch [data-ton], setting
     „mbb-ton“ shared with the home page),
   - the foil follows a mouse (hover-capable pointers only) or, on phones, the
     direction of gravity against a rest position that follows the grip; iOS asks
     for the motion permission on the first tap into the tile, never before.
   The only thing moving by itself is a very slight sway of the foil while the tile
   is on screen and nobody points at it, focuses or tilts it. Reduced motion: no
   tilt, no sway, no rolling — the number simply changes, the turn is instant (CSS).

   Starts on every [data-mbb-pd-holo], again on shopify:section:load; each element
   once. No globals: the guard against a second copy of this file is an attribute
   on <html>. Everything that moves uses transform or opacity. */
(() => {
  const html = document.documentElement;
  if (html.hasAttribute('data-mbb-pd-holo-js')) return;
  html.setAttribute('data-mbb-pd-holo-js', '');

  const ruhig = window.matchMedia('(prefers-reduced-motion: reduce)');
  const maus = window.matchMedia('(hover: hover)');
  const klemme = (v, a = 0, b = 1) => Math.min(b, Math.max(a, v));
  const mische = (a, b, t) => a + (b - a) * t;
  // Ticks of the number counter on the home page (ms): one digit clicks into place per interval.
  const TAKT = [0, 28, 58, 90, 126, 168, 220, 285];
  // About 15° of tilt from the rest position turn the foil all the way (home page).
  const EMPFINDLICH = 1.9;
  const gestartet = new WeakSet();

  /* ── Sound: the counter's clicks ─────────────────────────────────────
     The part of the home page's module klang (mbb-start.js) the counter needs, with
     its lessons: new AudioContext() blocks Chrome for up to 0.7 s, so the context is
     opened ahead, when the band comes near, and stays suspended until a click; a
     click only resumes it, and the very first roll waits until the sound runs (at
     most 0.5 s), so digits and clicks stay together. Without a prepared context the
     click creates it after the first frame (Safari: in the click). Sound off is the
     home page's setting („mbb-ton“ in localStorage). */
  const klang = (() => {
    const AC = window.AudioContext || window.webkitAudioContext;
    let an = true;
    try {
      an = localStorage.getItem('mbb-ton') !== 'aus';
    } catch (e) {}
    let ctx = null;
    let ziel = null;
    let rauschen = null;
    let anlauf = null;
    let erzeugt = false;
    let schlaf = 0;
    const zufall = (a, b) => a + Math.random() * (b - a);
    const leerlauf = window.requestIdleCallback || ((f) => setTimeout(f, 200));
    const kanal = () => {
      try {
        if (navigator.audioSession) navigator.audioSession.type = 'ambient';
        ctx = new AC();
      } catch (e) {
        ctx = null;
        return false;
      }
      const komp = ctx.createDynamicsCompressor();
      komp.threshold.value = -10;
      komp.knee.value = 6;
      komp.ratio.value = 4;
      komp.attack.value = 0.003;
      komp.release.value = 0.15;
      ziel = ctx.createGain();
      ziel.gain.value = 0.75;
      ziel.connect(komp).connect(ctx.destination);
      const sr = ctx.sampleRate;
      rauschen = ctx.createBuffer(1, sr, sr);
      const d = rauschen.getChannelData(0);
      for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
      return true;
    };
    // An idle context is put to sleep; the next click wakes it.
    const schlafen = () => {
      clearTimeout(schlaf);
      schlaf = setTimeout(() => {
        if (ctx && ctx.state === 'running') ctx.suspend();
      }, 8000);
    };
    const starte = () => {
      if (ctx.state !== 'running') anlauf = ctx.resume().catch(() => {});
      schlafen();
    };
    const vorbereiten = () => {
      if (!an || !AC || ctx) return;
      leerlauf(
        () => {
          if (!an || ctx || !kanal()) return;
          if (ctx.state === 'running') ctx.suspend();
        },
        { timeout: 2000 },
      );
    };
    // Mouse over the IDs, a press, keyboard focus: start the channel now if the page
    // has had a click already — then the click itself needs no wait.
    const wecken = () => {
      if (!an || !ctx || ctx.state === 'running') return;
      if (navigator.userActivation && navigator.userActivation.hasBeenActive) starte();
    };
    // In the click: wake the context, or have it created after the first frame.
    const bereit = () => {
      if (!an || !AC) return;
      if (ctx) {
        starte();
        return;
      }
      if (navigator.audioSession) {
        if (kanal()) starte();
        return;
      }
      if (erzeugt) return;
      erzeugt = true;
      anlauf = new Promise((fertig) =>
        requestAnimationFrame(() =>
          setTimeout(() => {
            if (!kanal()) return fertig();
            ctx.resume().catch(() => {}).then(fertig);
            schlafen();
          }, 0),
        ),
      );
    };
    // Resolves once a sound starts on time: at once when the channel runs or sound
    // is off, at the latest after max ms.
    const laeuft = (max) => {
      if (!an || !AC || !anlauf || (ctx && ctx.state === 'running')) return Promise.resolve();
      return Promise.race([anlauf, new Promise((fertig) => setTimeout(fertig, max))]);
    };
    const huelle = (g, t, spitze, anstieg, abfall) => {
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(Math.max(0.0002, spitze), t + anstieg);
      g.gain.exponentialRampToValueAtTime(0.0001, t + anstieg + abfall);
    };
    const ausgang = (pan) => {
      const p = ctx.createStereoPanner ? ctx.createStereoPanner() : ctx.createGain();
      if (p.pan) p.pan.value = pan;
      p.connect(ziel);
      return p;
    };
    const rausch = (t, typ, f, q, spitze, anstieg, abfall, pan = 0) => {
      if (t < ctx.currentTime) return;
      const quelle = ctx.createBufferSource();
      quelle.buffer = rauschen;
      quelle.loop = true;
      const filter = ctx.createBiquadFilter();
      filter.type = typ;
      filter.frequency.value = f;
      filter.Q.value = q;
      const g = ctx.createGain();
      huelle(g, t, spitze, anstieg, abfall);
      quelle.connect(filter).connect(g).connect(ausgang(pan));
      quelle.start(t, Math.random() * 0.9);
      quelle.stop(t + anstieg + abfall + 0.05);
    };
    const ton = (t, typ, f0, f1, gleit, spitze, anstieg, abfall, pan = 0) => {
      if (t < ctx.currentTime) return;
      const o = ctx.createOscillator();
      o.type = typ;
      o.frequency.setValueAtTime(f0, t);
      if (f1) o.frequency.exponentialRampToValueAtTime(f1, t + gleit);
      const g = ctx.createGain();
      huelle(g, t, spitze, anstieg, abfall);
      o.connect(g).connect(ausgang(pan));
      o.start(t);
      o.stop(t + anstieg + abfall + 0.05);
    };
    const laut = () => an && ctx && ctx.state === 'running';
    return {
      moeglich: Boolean(AC),
      get an() {
        return an;
      },
      schalte(wert) {
        an = wert;
        try {
          if (wert) localStorage.removeItem('mbb-ton');
          else localStorage.setItem('mbb-ton', 'aus');
        } catch (e) {}
        if (!wert && ctx && ctx.state === 'running') ctx.suspend();
      },
      vorbereiten,
      wecken,
      bereit,
      laeuft,
      // A number counter rattling onto the next ID, the last digit settles — the
      // home page's „zaehlwerk“, in the ticks the digits roll in (TAKT).
      zaehlwerk() {
        if (!laut()) return;
        const t = ctx.currentTime + 0.01;
        TAKT.slice(0, -1).forEach((ms, i) => {
          const s = 0.75 + (0.25 * i) / (TAKT.length - 1);
          rausch(t + ms / 1000, 'bandpass', zufall(2900, 3500), 4, 0.16 * s, 0.0008, 0.012, 0.15);
          ton(t + ms / 1000, 'sine', 2200, 0, 0, 0.025 * s, 0.0008, 0.01, 0.15);
        });
        const ende = t + TAKT[TAKT.length - 1] / 1000;
        rausch(ende, 'bandpass', 2600, 3, 0.22, 0.0008, 0.02, 0.1);
        ton(ende, 'sine', 900, 600, 0.02, 0.07, 0.001, 0.04, 0.1);
      },
      // The confirmation when sound is switched on.
      ping() {
        if (!laut()) return;
        const t = ctx.currentTime + 0.01;
        ton(t, 'sine', 1318.5, 0, 0, 0.08, 0.002, 0.25);
        ton(t, 'sine', 2637, 0, 0, 0.02, 0.002, 0.12);
      },
    };
  })();

  const starte = (root) => {
    if (gestartet.has(root)) return;
    const karte = root.querySelector('[data-karte]');
    const innen = root.querySelector('[data-innen]');
    const vorne = root.querySelector('[data-vorne]');
    const hinten = root.querySelector('[data-hinten]');
    const idEl = root.querySelector('[data-holo-id]');
    const eintraege = [...root.querySelectorAll('[data-holo-eintrag]')];
    const ids = eintraege.map((li) => li.textContent.trim()).filter(Boolean);
    if (!karte || !innen || !vorne || !idEl || ids.length === 0) return;
    gestartet.add(root);

    const drehen = root.querySelector('[data-drehen]');
    const naechstes = root.querySelector('[data-naechstes]');
    const ansage = root.querySelector('[data-ansage]');
    const ansageMuster = root.dataset.tAnsage || '[id]';

    let nr = 0;
    let gedreht = false;
    // After a turn the card keeps its slow transition for a moment before it follows again.
    let ruheBis = 0;

    /* ── Rolling digits (copied from the home page) ──────────────────
       Each digit is a window over a strip of its old digit, random digits and the
       new one, rolling up one place per interval of TAKT; they land left to right
       (ticks 3 to 7). Afterwards the number is plain text again. Screen readers get
       the new number at once, the strips are hidden from them. */
    let idJetzt = idEl.textContent.trim();
    let rollt = [];
    const setzeId = (neu, verzoegerung) => {
      rollt.forEach((a) => a.cancel());
      rollt = [];
      const alt = idJetzt;
      idJetzt = neu;
      if (verzoegerung == null || !alt || ruhig.matches || typeof idEl.animate !== 'function') {
        idEl.textContent = neu;
        return;
      }
      const zufallsziffer = () => String(Math.floor(Math.random() * 10));
      const ziffer = (c) => c >= '0' && c <= '9';
      const anzahl = [...neu].filter(ziffer).length;
      const dauer = TAKT[TAKT.length - 1] + 20;
      const sichtbar = document.createElement('span');
      sichtbar.setAttribute('aria-hidden', 'true');
      let k = 0;
      [...neu].forEach((c, i) => {
        const rad = document.createElement('span');
        rad.className = 'mbb-karte__rad';
        sichtbar.append(rad);
        if (!ziffer(c)) {
          rad.textContent = c;
          return;
        }
        const landet = 3 + Math.round((k++ * 4) / Math.max(1, anzahl - 1));
        const folge = [ziffer(alt[i] || '') ? alt[i] : zufallsziffer()];
        for (let s = 1; s < landet; s++) folge.push(zufallsziffer());
        folge.push(c);
        const band = document.createElement('span');
        band.textContent = folge.join('\n');
        rad.append(band);
        // One digit per interval, the last step eases out; the explicit last frame keeps
        // the strip on the new digit (WAAPI would otherwise end on the starting value).
        const bild = [];
        for (let s = 0; s < landet; s++) {
          bild.push({
            transform: `translateY(${-s}em)`,
            offset: TAKT[s] / dauer,
            easing: s === landet - 1 ? 'cubic-bezier(0.2, 0.7, 0.3, 1)' : 'linear',
          });
        }
        bild.push({ transform: `translateY(${-landet}em)`, offset: TAKT[landet] / dauer });
        bild.push({ transform: `translateY(${-landet}em)`, offset: 1 });
        rollt.push(band.animate(bild, { duration: dauer, delay: verzoegerung * 1000, fill: 'forwards' }));
      });
      const vorlesen = document.createElement('span');
      vorlesen.className = 'visually-hidden';
      vorlesen.textContent = neu;
      idEl.replaceChildren(vorlesen, sichtbar);
      const diese = rollt;
      Promise.all(diese.map((a) => a.finished)).then(
        () => {
          if (rollt !== diese) return;
          idEl.textContent = neu;
          rollt = [];
        },
        () => {},
      );
    };

    const zeige = (i, rollen) => {
      setzeId(ids[i], rollen);
      eintraege.forEach((li, k) => {
        if (k === i) li.setAttribute('aria-current', 'true');
        else li.removeAttribute('aria-current');
        const wahl = li.querySelector('[data-holo-wahl]');
        if (wahl) wahl.setAttribute('aria-pressed', String(k === i));
      });
      if (ansage) {
        ansage.textContent = ansageMuster
          .replace('[id]', ids[i])
          .replace('[nr]', String(i + 1))
          .replace('[n]', String(ids.length));
      }
    };

    const seite = () => {
      karte.classList.remove('folgt');
      ruheBis = performance.now() + 800;
      karte.classList.toggle('ist-gedreht', gedreht);
      vorne.inert = gedreht;
      if (hinten) hinten.inert = !gedreht;
      // No aria-pressed: the button's label itself changes with the side (CSS).
    };
    const umdrehen = () => {
      if (!hinten) return;
      gedreht = !gedreht;
      seite();
    };

    // iOS hands out motion data only after asking, and only inside a user gesture.
    // Asked once, on the first tap into the tile (also the tap that turns the card).
    let gefragt = false;
    root.addEventListener(
      'click',
      () => {
        if (gefragt || maus.matches || ruhig.matches) return;
        gefragt = true;
        const D = window.DeviceOrientationEvent;
        if (D && typeof D.requestPermission === 'function') D.requestPermission().catch(() => {});
      },
      { capture: true },
    );

    drehen?.addEventListener('click', umdrehen);
    karte.addEventListener('click', (e) => {
      if (e.target.closest('a, button')) return;
      umdrehen();
    });
    naechstes?.addEventListener('click', () => {
      nr = (nr + 1) % ids.length;
      // Turned over: back to the front first, the counter starts once the turn has passed its peak.
      const zurueck = gedreht;
      if (gedreht) {
        gedreht = false;
        seite();
      }
      zeige(nr, zurueck ? 0.12 : 0);
    });
    // A click on an ID: the counter's clicks and the rolling digits start together.
    // The press already wakes the sound (it counts as a user action), and the very
    // first roll waits until the sound runs — measured up to 0.76 s after the click
    // when the click is the first action on the page, hence 0.9 s at most.
    const wahlen = [...root.querySelectorAll('[data-holo-wahl]')];
    wahlen.forEach((k) => {
      k.addEventListener('pointerdown', () => klang.bereit());
      k.addEventListener('click', () => {
        const i = Number(k.dataset.holoWahl);
        if (i === nr) return;
        nr = i;
        klang.bereit();
        klang.laeuft(900).then(() => {
          if (nr !== i) return;
          klang.zaehlwerk();
          zeige(nr, 0);
        });
      });
    });
    const liste = root.querySelector('.mbb-pd-holo__ids');
    if (liste && wahlen.length && klang.moeglich) {
      if ('IntersectionObserver' in window) {
        const nah = new IntersectionObserver(
          (e) => {
            if (!e.some((x) => x.isIntersecting)) return;
            nah.disconnect();
            klang.vorbereiten();
          },
          { rootMargin: '400px 0px' },
        );
        nah.observe(root);
      } else {
        klang.vorbereiten();
      }
      liste.addEventListener('pointerover', (e) => {
        if (e.pointerType === 'mouse') klang.wecken();
      });
      liste.addEventListener('pointerdown', () => klang.wecken());
      liste.addEventListener('focusin', () => klang.wecken());
    }
    // Sound on/off, as on the home page's card.
    const tonKnopf = root.querySelector('[data-ton]');
    if (tonKnopf && klang.moeglich) {
      const zeigeTon = () => {
        tonKnopf.setAttribute('aria-pressed', String(klang.an));
        tonKnopf.title = klang.an ? tonKnopf.dataset.tAus : tonKnopf.dataset.tAn;
      };
      zeigeTon();
      tonKnopf.hidden = false;
      tonKnopf.addEventListener('click', () => {
        klang.schalte(!klang.an);
        zeigeTon();
        if (klang.an) {
          klang.bereit();
          klang.laeuft(500).then(() => klang.ping());
        }
      });
    }

    /* ── Tilt ────────────────────────────────────────────────────────── */
    if (ruhig.matches) return;

    const setze = (x, y, kipp = 1) => {
      innen.style.setProperty('--ry', `${(x - 0.5) * 26 * kipp}deg`);
      innen.style.setProperty('--rx', `${(0.5 - y) * 18 * kipp}deg`);
      innen.style.setProperty('--mx', `${x * 100}%`);
      innen.style.setProperty('--my', `${y * 100}%`);
      // The seal's motifs: bear to the right, „@“ to the left; both faint when the card rests.
      innen.style.setProperty('--kx', ((x - 0.5) * 2 * Math.min(1, kipp * 2)).toFixed(3));
    };
    const ruhe = () => setze(0.5, 0.4, 0);

    let zeigt = false; // a mouse is over the card
    let drin = false; // a mouse is over the tile
    let fokus = false; // keyboard focus inside the tile
    let sichtbar = false;
    let laeuft = false;

    karte.addEventListener('pointermove', (e) => {
      if (e.pointerType === 'touch' || !maus.matches || ruhig.matches) return;
      const r = karte.getBoundingClientRect();
      zeigt = true;
      if (performance.now() > ruheBis) karte.classList.add('folgt');
      setze(klemme((e.clientX - r.left) / r.width), klemme((e.clientY - r.top) / r.height));
    });
    karte.addEventListener('pointerleave', () => {
      if (!zeigt) return;
      zeigt = false;
      karte.classList.remove('folgt');
      ruhe();
    });
    root.addEventListener('pointerenter', (e) => {
      if (e.pointerType !== 'touch') drin = true;
    });
    root.addEventListener('pointerleave', () => {
      drin = false;
    });
    root.addEventListener('focusin', () => {
      fokus = true;
    });
    root.addEventListener('focusout', (e) => {
      fokus = root.contains(e.relatedTarget);
    });

    // Phones: not the angles themselves — gamma flips by up to 180° near upright, and
    // a fixed rest position put a steeper grip at the stop at once (home page,
    // 03.11.2026). Instead the direction of gravity in the screen plane, measured
    // against a rest position that follows the grip within a few seconds, so it is
    // the movement that turns the foil; smoothed per frame in the tick.
    let schwere = null;
    let ruhelage = null;
    let neigung = null;
    const lage = (e) => {
      if (!root.isConnected) {
        window.removeEventListener('deviceorientation', lage);
        return;
      }
      if (e.gamma == null || e.beta == null) return;
      const b = (e.beta * Math.PI) / 180;
      const g = (e.gamma * Math.PI) / 180;
      const gx = Math.cos(b) * Math.sin(g);
      const gy = -Math.sin(b);
      // Landscape: the screen's axes are the device's turned by the screen angle.
      const w = (((screen.orientation && screen.orientation.angle) || window.orientation || 0) * Math.PI) / 180;
      schwere = { x: gx * Math.cos(w) + gy * Math.sin(w), y: gy * Math.cos(w) - gx * Math.sin(w) };
      if (!ruhelage) ruhelage = { ...schwere };
    };
    if (!maus.matches) window.addEventListener('deviceorientation', lage, { passive: true });

    const tick = (t) => {
      laeuft = false;
      if (!sichtbar || !root.isConnected || ruhig.matches) return;
      if (!zeigt && performance.now() > ruheBis) {
        if (schwere) {
          karte.classList.add('folgt');
          ruhelage.x = mische(ruhelage.x, schwere.x, 0.006);
          ruhelage.y = mische(ruhelage.y, schwere.y, 0.006);
          const ziel = {
            x: klemme(0.5 + (schwere.x - ruhelage.x) * EMPFINDLICH),
            y: klemme(0.45 - (schwere.y - ruhelage.y) * EMPFINDLICH),
          };
          neigung = neigung ? { x: mische(neigung.x, ziel.x, 0.18), y: mische(neigung.y, ziel.y, 0.18) } : ziel;
          setze(neigung.x, neigung.y);
        } else if (!drin && !fokus) {
          // Idle: a very slight, slow sway so the foil shows itself (smaller and slower
          // than on the home page — on the product page the card is not the stage).
          karte.classList.add('folgt');
          setze(0.5 + Math.sin(t / 2600) * 0.18, 0.45 + Math.cos(t / 3400) * 0.12, 0.35);
        }
      }
      laeuft = true;
      requestAnimationFrame(tick);
    };

    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((e) => {
        if (!root.isConnected) {
          io.disconnect();
          return;
        }
        sichtbar = e[e.length - 1].isIntersecting;
        if (sichtbar && !laeuft) {
          laeuft = true;
          requestAnimationFrame(tick);
        }
      });
      io.observe(root);
    }
    // Switching to reduced motion while the page is open: back to rest, the tick stops itself.
    ruhig.addEventListener?.('change', () => {
      if (!ruhig.matches) return;
      karte.classList.remove('folgt');
      ruhe();
    });
  };

  const alle = (bereich) => {
    if (bereich.matches && bereich.matches('[data-mbb-pd-holo]')) starte(bereich);
    bereich.querySelectorAll('[data-mbb-pd-holo]').forEach(starte);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => alle(document.documentElement));
  else alle(document.documentElement);
  document.addEventListener('shopify:section:load', (e) => alle(e.target));
})();
